import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RowActions from '../components/RowActions';
import { CompanyContext } from '../contexts/CompanyContext';
import { downloadEntityPdf } from '../lib/entityPdf';
import BuildingFilter from '../components/BuildingFilter';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'];

export default function Schedules() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const { company } = useContext(CompanyContext);
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newS, setNewS] = useState({
    title: '', work_type: 'cleaning', frequency: 'daily',
    building_id: '', assigned_to: '', notes: '',
    next_due: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchData(); }, [typeFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = typeFilter !== 'all' ? `?work_type=${typeFilter}` : '';
      const [s, b, st] = await Promise.all([
        axios.get(`${API}/work-schedules${q}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true })
      ]);
      setItems(s.data); setBuildings(b.data); setStaff(st.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/work-schedules`, {
        ...newS,
        next_due: new Date(newS.next_due).toISOString(),
        building_id: newS.building_id || null,
        assigned_to: newS.assigned_to || null
      }, { withCredentials: true });
      toast.success('Schedule created');
      setShowNew(false);
      setNewS({ title: '', work_type: 'cleaning', frequency: 'daily', building_id: '', assigned_to: '', notes: '', next_due: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const complete = async (id) => {
    try {
      await axios.put(`${API}/work-schedules/${id}/complete`, {}, { withCredentials: true });
      toast.success('Marked complete · next due auto-scheduled');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete schedule?')) return;
    try { await axios.delete(`${API}/work-schedules/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const isOverdue = (d) => new Date(d) < new Date();

  return (
    <Layout>
      <div data-testid="schedules-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('sched.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('sched.subtitle')}</p>
            <p className="text-sm text-muted-foreground">Cleaning, security and technician routines · daily → annual · auto-renew on completion</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild><Button data-testid="new-schedule-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Schedule</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create Work Schedule</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Title</Label><Input data-testid="sch-title" value={newS.title} onChange={(e) => setNewS({ ...newS, title: e.target.value })} placeholder="e.g., Lobby deep cleaning" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Type</Label>
                      <Select value={newS.work_type} onValueChange={(v) => setNewS({ ...newS, work_type: v })}>
                        <SelectTrigger data-testid="sch-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cleaning">Cleaning</SelectItem>
                          <SelectItem value="security">Security</SelectItem>
                          <SelectItem value="technician">Technician</SelectItem>
                          <SelectItem value="inspection">Inspection</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Frequency</Label>
                      <Select value={newS.frequency} onValueChange={(v) => setNewS({ ...newS, frequency: v })}>
                        <SelectTrigger data-testid="sch-freq"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Building (opt)</Label>
                      <Select value={newS.building_id} onValueChange={(v) => setNewS({ ...newS, building_id: v })}>
                        <SelectTrigger><SelectValue placeholder="All buildings" /></SelectTrigger>
                        <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Assign to (opt)</Label>
                      <Select value={newS.assigned_to} onValueChange={(v) => setNewS({ ...newS, assigned_to: v })}>
                        <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Next Due</Label><Input type="date" value={newS.next_due} onChange={(e) => setNewS({ ...newS, next_due: e.target.value })} /></div>
                  <div><Label>Notes</Label><Textarea rows={2} value={newS.notes} onChange={(e) => setNewS({ ...newS, notes: e.target.value })} /></div>
                  <Button data-testid="submit-sch-btn" onClick={handleCreate} disabled={!newS.title} className="w-full bg-teal-600 hover:bg-teal-700">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          {['all', 'cleaning', 'security', 'technician', 'inspection'].map(f => (
            <button key={f} data-testid={`sch-filter-${f}`} onClick={() => setTypeFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition capitalize ${typeFilter === f ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200'}`}>{f}</button>
          ))}
          <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} className="ml-auto" />
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid gap-3">
            {items.filter(s => filterBuildingId === 'all' || s.building_id === filterBuildingId).map(s => {
              const overdue = isOverdue(s.next_due);
              const staffObj = staff.find(x => x.id === s.assigned_to);
              const buildingObj = buildings.find(x => x.id === s.building_id);
              return (
                <Card key={s.id} data-testid={`sch-card-${s.id}`} className={`p-4 ${overdue ? 'border-l-4 border-l-red-500' : ''}`}>
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="w-4 h-4 text-teal-600" />
                        <h3 className="font-semibold text-foreground">{s.title}</h3>
                        {overdue && <span className="flex items-center gap-1 text-xs text-red-600 font-semibold"><AlertTriangle className="w-3 h-3" />Overdue</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs mt-2">
                        <span className="px-2 py-1 rounded-full bg-teal-100 text-teal-800 capitalize">{s.work_type}</span>
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 capitalize">{s.frequency.replace('_', '-')}</span>
                        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">Next: {new Date(s.next_due).toLocaleDateString()}</span>
                        {buildingObj && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">{buildingObj.name}</span>}
                        {staffObj && <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700">👤 {staffObj.name}</span>}
                      </div>
                      {s.notes && <p className="text-xs text-muted-foreground mt-2">{s.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`complete-sch-${s.id}`} onClick={() => complete(s.id)} size="sm" variant="outline" className="text-green-600 border-green-600"><CheckCircle2 className="w-4 h-4 mr-1" />Complete</Button>
                      <RowActions
                        testIdPrefix={`sch-${s.id}`}
                        onDownloadPdf={() => downloadEntityPdf({
                          title: `Schedule · ${s.title}`,
                          subtitle: `${s.work_type} · ${s.frequency}`,
                          company,
                          fields: [
                            { label: 'Title', value: s.title },
                            { label: 'Work type', value: s.work_type },
                            { label: 'Frequency', value: s.frequency },
                            { label: 'Building', value: buildingObj?.name || '—' },
                            { label: 'Assigned to', value: staffObj?.name || '—' },
                            { label: 'Next due', value: new Date(s.next_due).toLocaleDateString() },
                          ],
                          notes: s.notes,
                        })}
                        onDelete={MANAGE.includes(user?.role) ? () => handleDelete(s.id) : undefined}
                        deleteLabel={`schedule "${s.title}"`}
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && <Card className="p-10 text-center"><Calendar className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No schedules</p></Card>}
          </div>
        )}
      </div>
    </Layout>
  );
}
